import { useCallback } from 'react';
import { type NavigateOptions, useSearchParams } from 'react-router';

import { createCustomSearchParamsUpdater } from './createCustomSearchParamsUpdater';

export type Setter<T extends object> = (nextInit: T | ((prev: T) => T), navigateOpts?: NavigateOptions) => void;

export function createSearchParamsHook<T extends object>({
  parse,
  serialize,
  navigateOpts,
}: {
  parse: (searchParams: URLSearchParams) => T;
  serialize: (t: T) => URLSearchParams;
  navigateOpts?: NavigateOptions | undefined;
}) {
  return function useSearchParamsInner(defaultInit?: T) {
    const [searchParams, setSearchParams] = useSearchParams(
      defaultInit !== undefined ? serialize(defaultInit) : undefined,
    );

    const createSearchParams = useCallback(
      (init: T | ((prev: T) => T)) => {
        const updater = createCustomSearchParamsUpdater({ parse, serialize });
        return updater(searchParams, init);
      },
      [searchParams],
    );

    const setCustomSearchParams = useCallback(
      (init: T | ((prev: T) => T), navigateOptsArg?: NavigateOptions) => {
        setSearchParams(createSearchParams(init), navigateOptsArg ?? navigateOpts);
      },
      [createSearchParams, setSearchParams],
    );

    return [parse(searchParams), setCustomSearchParams, createSearchParams] as const;
  };
}
